import { Component, h, Host, Element, Prop } from '@stencil/core';
import { AhaEzTab } from '../aha-ez-tab/aha-ez-tab';

function clickableTab(tab: HTMLAhaEzTabElement) {
  return !tab.hasAttribute('disabled') && tab.ariaDisabled !== 'true' && !tab.inert;
}

// Aria keyboard navigation for tabs.
document.addEventListener('keydown', e => {
  const tabList = e.composedPath().find((elem: any) => elem instanceof AhaEzTabList);
  if (tabList && tabList instanceof AhaEzTabList) {
    const tabs = tabList.tabs;
    const selectedTab = tabs.find(tab => tab.ariaSelected === 'true');
    if (selectedTab) {
      const selectedIndex = tabs.indexOf(selectedTab);
      let nextIndex;
      let tab;

      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = selectedIndex - 1;
          if (nextIndex >= 0) {
            tab = tabs[nextIndex];
          }
          break;
        case 'ArrowRight':
          nextIndex = selectedIndex + 1;
          if (nextIndex < tabs.length) {
            tab = tabs[nextIndex];
          }
          break;
        case 'Home':
          tab = tabs.find(tab => clickableTab(tab));
          break;
        case 'End':
          tab = tabs.reverse().find(tab => clickableTab(tab));
          break;
      }
      if (tab) {
        tab.click();
        tab.focus();
      }
    }
  }
});

export interface AhaEzTabList extends HTMLAhaEzTabListElement {}

@Component({
  tag: 'aha-ez-tab-list',
  styleUrl: 'aha-ez-tab-list.css',
  shadow: true,
})
export class AhaEzTabList {
  @Element() host!: HTMLAhaEzTabListElement;

  private tabsSlot?: any;

  @Prop({ reflect: true }) flex?: boolean;

  /** Automatically hide the aha-ez-tab-list component if it only has one child aha-ez-tab. */
  @Prop({ reflect: true }) autoHide?: boolean;

  connectedCallback() {
    this.selectInitialTabWhenNoTabSelected = this.selectInitialTabWhenNoTabSelected.bind(this);
  }

  get tabs() {
    return Array.from(this.host.querySelectorAll<HTMLAhaEzTabElement>(':scope aha-ez-tab'));
  }

  get selectedTab() {
    const tabs = this.tabs;
    return tabs.find(tab => tab instanceof AhaEzTab && tab.ariaSelected === 'true');
  }

  get selectedTabIndex() {
    if (this.selectedTab) return this.tabs.indexOf(this.selectedTab);
  }

  // If no tab in an aha-ez-tabs is currently selected manually via a `aria-selected="true"` attribute, then select the first non-disabled, non-inert, non-hidden tab.
  private selectInitialTabWhenNoTabSelected() {
    let selectedTab = this.selectedTab;
    if (!selectedTab) {
      const tabs = this.tabs;
      selectedTab = tabs.find(
        tab => !tab.hasAttribute('disabled') && tab.ariaDisabled !== 'true' && !tab.inert,
      );
      if (selectedTab instanceof AhaEzTab) selectedTab.selected = true;
    }
  }

  render() {
    return (
      <Host slot="tablist" role="tablist">
        <slot
          ref={e => (this.tabsSlot = e)}
          onSlotchange={this.selectInitialTabWhenNoTabSelected}
        ></slot>
      </Host>
    );
  }
}
